import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

const OrderDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const order_id = searchParams.get('order_id');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!order_id) {
      alert('잘못된 접근입니다.');
      navigate('/order/list');
      return;
    }

    const fetchOrderDetail = async () => {
      try {
        const res = await axios.get(`/api/order/detail?order_id=${order_id}`);
        setData(res.data);
      } catch (err) {
        alert(err.response?.data?.message || '데이터를 불러오지 못했습니다.');
        navigate('/order/list');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetail();
  }, [order_id, navigate]);

  if (loading) return <div className="container mt-5 text-center">로딩 중...</div>;
  if (!data) return null;

  const {
    order,
    delivery,
    delivery_status,
    courier,
    tracking_number,
    order_items,
    is_cancelled,
    steps,
    active_index,
    is_pickup,
    pickup_datetime,
    cs_label,
  } = data;

  const originalPrice =
  Number(order.total_price || 0)
  + Number(order.product_discount_amount || 0);

  const optionTotalPrice = order_items.reduce((sum, item) => {
    return sum + (
      Number(item.option_total || 0) *
      Number(item.quantity || 0)
    );
  }, 0);
  
  const productOriginalPrice =
    originalPrice - optionTotalPrice;

  const handleReceipt = async () => {
    try {
      const res = await axios.post('/api/pay/user/receipt', {
        paykey: order.paymentkey,
      });

      if (!res.data.success) {
        alert(res.data.message || '영수증 조회 실패');
        return;
      }

      const receiptUrl = res.data.receipt_url;

      if (!receiptUrl) {
        alert('영수증 URL이 존재하지 않습니다.');
        return;
      }

      window.open(receiptUrl, '_blank');
    } catch (err) {
      alert(err.response?.data?.message || '영수증 조회 중 오류가 발생했습니다.');
    }
  };

  const displayDeliveryCost =
  Number(order.delivery_cost || 0);

  const handleCancelOrder = async () => {
    if (!window.confirm('정말 결제를 취소하시겠습니까?')) return;

    try {
      await axios.post('/api/pay/user/cancel', {
        cancel_order_id: order.paymentkey,
        cancel_id: order.id,
      });
      alert('취소 요청이 완료되었습니다.');
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.message || '취소 처리 중 오류가 발생했습니다.');
    }
  };

  const formatAddress = (address) => {
    if (!address) return '-';
    return String(address)
      .split('\n')
      .map((line, idx) => (
        <React.Fragment key={idx}>
          {line}
          {idx !== String(address).split('\n').length - 1 && <br />}
        </React.Fragment>
      ));
  };

  return (
    <div className="container" style={styles.container}>
      <h2 style={styles.title}>
        <Link to="/order/list" style={styles.linkBack}>주문내역</Link> &gt; 상세내역
      </h2>

      <div style={styles.card}>
        <div style={styles.header}>
          <div>
            <div style={styles.orderNumber}>주문번호: {order.id}</div>
            <div style={styles.orderDate}>{order.order_date}</div>
          </div>
        </div>

        {is_cancelled ? (
          <div style={styles.cancelNotice}>주문이 취소되었습니다</div>
        ) : is_pickup ? (
          <>
            {!pickup_datetime && (
              <div style={styles.pickupStatusBox}>
                <div style={styles.pickupLabel}>주문 진행상태</div>
                <div style={styles.pickupStatusText}>{cs_label}</div>
              </div>
            )}
        
            {pickup_datetime && (
              <div style={styles.pickupDatetimeBox}>
                <div style={styles.pickupLabel}>픽업 예약일시</div>
                <div style={styles.pickupDatetimeText}>{pickup_datetime}</div>
              </div>
            )}
          </>
        ) : (
          <div style={styles.deliverySteps}>
            {steps.map((step, idx) => (
              <div
                key={idx}
                style={{
                  ...styles.step,
                }}
              >
                <div
                  style={{
                    ...styles.circle,
                    ...(idx <= active_index ? styles.activeCircle : {}),
                  }}
                >
                  {idx + 1}
                </div>
                <span style={styles.stepText}>{step}</span>
                {idx < steps.length - 1 && (
                  <div
                    style={{
                      ...styles.stepLine,
                      ...(idx <= active_index ? styles.activeLine : {}),
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <table style={styles.table}>
          <tbody>
            {!is_cancelled &&
              ['배송중', '배송완료'].includes(delivery_status) &&
              courier &&
              tracking_number && (
                <tr>
                  <th style={styles.th}>배송상태</th>
                  <td style={styles.td}>
                    <div style={styles.flexBetween}>
                      <span>{delivery_status}</span>
                      <button
                        style={styles.subBtn}
                        onClick={() =>
                          window.open(
                            `https://search.naver.com/search.naver?query=${encodeURIComponent(`${courier} ${tracking_number}`)}`,
                            '_blank'
                          )
                        }
                      >
                        배송조회
                      </button>
                    </div>
                  </td>
                </tr>
              )}

            <tr>
              <th style={styles.th}>수령인</th>
              <td style={styles.td}>{delivery?.receiver_name || '-'}</td>
            </tr>
            <tr>
              <th style={styles.th}>전화번호</th>
              <td style={styles.td}>{delivery?.receiver_phone || '-'}</td>
            </tr>
            <tr>
              <th style={styles.th}>주소</th>
              <td style={styles.td}>{formatAddress(delivery?.address)}</td>
            </tr>
            <tr>
              <th style={styles.th}>결제방법</th>
              <td style={styles.td}>
                <div style={styles.flexBetween}>
                  <span>{order.payment_method || '-'}</span>
                  {['결제완료', '주문취소'].includes(order.status) && (
                    <button style={styles.subBtn} onClick={handleReceipt}>
                      영수증
                    </button>
                  )}
                </div>
              </td>
            </tr>
            <tr>
              <th style={styles.th}>결제금액</th>
              <td style={styles.td}>
                <div style={styles.flexBetween}>
                  <span>{Number(order.result_price || 0).toLocaleString()}원</span>
                  {order.status === '결제완료' &&
                    !['배송준비중', '배송중', '배송완료'].includes(delivery_status) && (
                      <div style={styles.cancelBtnWrap}>
                        <button style={styles.subBtn} onClick={handleCancelOrder}>
                          결제취소요청
                        </button>
                      </div>
                    )}
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div style={styles.productList}>
          {order_items?.length > 0 ? (
            order_items.map((item, idx) => (
              <div key={idx} style={styles.productItem}>
                <div style={styles.productThumb}>
                  <img
                    src={
                      item.image_path
                        ? item.image_path.startsWith('http')
                          ? item.image_path
                          : `/static/${item.image_path}`
                        : '/static/default_product.png'
                    }
                    alt={item.name}
                    style={styles.thumbImg}
                  />
                </div>

                <div style={styles.productInfo}>
                  <div style={styles.productName}>{item.name}</div>

                  {item.options?.length > 0 && (
                    <div style={styles.optionBox}>
                      {item.options.map((opt, optIdx) => (
                        <div key={optIdx} style={styles.optionText}>
                          {opt.option_group_name}: {opt.option_item_name}
                          {Number(opt.option_price || 0) > 0 &&
                            ` (+${Number(opt.option_price || 0).toLocaleString()}원)`
                          }
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={styles.productQty}>수량: {item.quantity}</div>

                  <div style={styles.productPrice}>
                    가격: {(
                      (Number(item.price || 0) + Number(item.option_total || 0))
                      * Number(item.quantity || 0)
                    ).toLocaleString()}원
                  </div>
                </div>

                {delivery_status === '배송완료' && (
                  <div style={styles.itemActions}>
                    <button
                      style={styles.subBtn}
                      onClick={() =>
                        navigate(`/claim/request?order_id=${order.id}&item_id=${item.id}`)
                      }
                    >
                      교환반품
                    </button>
                    <button
                      style={styles.subBtn}
                      onClick={() => navigate(`/review/write?product_id=${item.product_id}`)}
                    >
                      리뷰작성
                    </button>
                  </div>
                )}
              </div>
            ))
          ) : (
            <p>주문한 상품이 없습니다.</p>
          )}
        </div>

        <div style={styles.priceSummary}>
          <div style={styles.priceRow}>
            <span>상품금액</span>
            <span>{productOriginalPrice.toLocaleString()}원</span>
          </div>

          {optionTotalPrice > 0 && (
            <div style={styles.priceRow}>
              <span>옵션금액</span>
              <span>+{optionTotalPrice.toLocaleString()}원</span>
            </div>
          )}

          <div style={styles.priceRow}>
            <span>상품할인</span>
            <span>
              -{Number(order.product_discount_amount || 0).toLocaleString()}원
            </span>
          </div>

          <div style={styles.priceRow}>
            <span>쿠폰할인</span>
            <span>
              -{Number(order.discount_amount || 0).toLocaleString()}원
            </span>
          </div>

          <div style={styles.priceRow}>
            <span>배송료</span>
            <span>+{displayDeliveryCost.toLocaleString()}원</span>
          </div>

          <div style={styles.priceDivider}></div>

          <div style={styles.totalRow}>
            <span>최종 결제금액</span>
            <span>
              {Number(order.result_price || 0).toLocaleString()}원
            </span>
          </div>
        </div>  

      </div>
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
    maxWidth: '600px',
    margin: '0 auto',
    padding: '0 0px 40px',
    boxSizing: 'border-box',
  },
  title: {
    fontSize: '1.1rem',
    marginTop: '20px',
    marginLeft: '12px',
    marginRight: '12px',
  },
  linkBack: {
    textDecoration: 'none',
    color: '#000',
  },
  card: {
    background: '#fff',
    marginTop: '20px',
    borderRadius: '1px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
    padding: '20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #eee',
    paddingBottom: '10px',
    marginBottom: '15px',
  },
  orderNumber: {
    fontWeight: 'bold',
    fontSize: '1.1rem',
  },
  orderDate: {
    fontSize: '0.8rem',
    color: '#888',
  },
  cancelNotice: {
    color: '#c00',
    fontWeight: 'bold',
    textAlign: 'center',
    margin: '30px 0',
    fontSize: '16px',
  },
  pickupStatusBox: {
    margin: '18px -2px 10px',
    padding: '12px',
    background: '#f3f4f6',
    borderRadius: '0px',
  },
  pickupDatetimeBox: {
    margin: '0 -2px 14px',
    padding: '12px',
    background: '#eef4ff',
    borderRadius: '0px',
  },
  pickupLabel: {
    color: '#6c757d',
    fontSize: '12px',
    marginBottom: '4px',
  },
  pickupStatusText: {
    fontWeight: 'bold',
    fontSize: '16px',
  },
  pickupDatetimeText: {
    fontWeight: 'bold',
    fontSize: '16px',
    color: '#2f5fd7',
  },
  deliverySteps: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '30px 0 20px',
    position: 'relative',
  },
  step: {
    textAlign: 'center',
    flex: 1,
    position: 'relative',
  },
  stepText: {
    fontSize: '12px',
  },
  circle: {
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    background: '#ddd',
    color: '#fff',
    fontSize: '13px',
    fontWeight: 'bold',
    lineHeight: '27px',
    margin: '0 auto 5px',
    position: 'relative',
    zIndex: 1,
  },
  activeCircle: {
    background: '#00704a',
  },
  stepLine: {
    position: 'absolute',
    top: '12px',
    left: '50%',
    width: '100%',
    height: '2px',
    background: '#ddd',
    transform: 'translateX(0)',
    zIndex: 0,
  },
  activeLine: {
    background: '#00704a',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '25px',
    fontSize: '14px',
    tableLayout: 'fixed',
  },
  th: {
    width: '20%',
    textAlign: 'left',
    padding: '15px 0px',
    borderBottom: '1px solid #eee',
    verticalAlign: 'top',
    whiteSpace: 'nowrap',
  },
  td: {
    textAlign: 'left',
    padding: '15px 0px',
    borderBottom: '1px solid #eee',
    verticalAlign: 'top',
    wordWrap: 'break-word',
  },
  flexBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
  },
  subBtn: {
    padding: '2px 8px',
    fontSize: '12px',
    lineHeight: 1.2,
    borderRadius: '2px',
    border: '1px solid #ddd',
    background: '#fff',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  cancelBtnWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '60%',
    marginLeft: 'auto',
  },
  productList: {
    borderTop: 'none',
    paddingTop: '12px',
  },
  productItem: {
    display: 'flex',
    marginBottom: '14px',
    alignItems: 'flex-start',
  },
  productThumb: {
    width: '64px',
    height: '64px',
    borderRadius: '5px',
    overflow: 'hidden',
    marginRight: '12px',
    background: '#fefefe',
    border: '0px solid #ddd',
    flexShrink: 0,
  },
  thumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  productInfo: {
    flexGrow: 1,
  },
  productName: {
    fontWeight: 'bold',
    marginBottom: '4px',
    fontSize: '14px',
  },
  productQty: {
    color: '#666',
    fontSize: '13px',
  },
  productPrice: {
    color: '#666',
    fontSize: '13px',
  },
  itemActions: {
    width: '60%',
    display: 'flex',
    gap: '5px',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginLeft: 'auto',
  },
  priceSummary: {
    marginTop: '20px',
    borderTop: '1px solid #eee',
    paddingTop: '15px',
  },
  
  priceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontSize: '14px',
    color: '#555',
  },
  
  priceDivider: {
    height: '1px',
    background: '#eee',
    margin: '12px 0',
  },
  
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontWeight: 'bold',
    fontSize: '16px',
  },
  optionBox: {
    marginTop: '4px',
    marginBottom: '3px',
  },
  
  optionText: {
    color: '#777',
    fontSize: '12px',
    lineHeight: '1.4',
  },
};

export default OrderDetail;